import { Routes } from '@angular/router';
import {
  adminAccessGuard, adminGuard, customerGuard, platformGuard, storeGuard, storeModuleGuard, superAdminGuard, superAdminLoginGuard
} from './core/guards/auth.guard';

export const routes: Routes = [
  // ---------------------------------------------------------------------------
  // Plataforma: divulgação do sistema, cadastro e login das empresas
  // ---------------------------------------------------------------------------
  {
    path: '',
    pathMatch: 'full',
    canActivate: [platformGuard],
    loadComponent: () => import('./features/platform/landing/landing.component').then(m => m.LandingComponent)
  },
  {
    path: 'cadastro', // Cadastro da empresa (administrador), com 30 dias de teste
    canActivate: [platformGuard],
    loadComponent: () => import('./features/platform/company-signup/company-signup.component').then(m => m.CompanySignupComponent)
  },
  {
    path: 'entrar', // Login do administrador
    canActivate: [platformGuard],
    data: { admin: true },
    loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent)
  },

  // ---------------------------------------------------------------------------
  // Painel da empresa
  // ---------------------------------------------------------------------------
  {
    path: 'admin',
    canActivate: [adminGuard],
    loadComponent: () => import('./features/admin/dashboard/dashboard.component').then(m => m.DashboardComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'orders' },
      {
        path: 'orders',
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/orders-manager/orders-manager.component').then(m => m.OrdersManagerComponent)
      },
      {
        path: 'production', // Tela da cozinha: pedidos Em Produção
        canActivate: [adminAccessGuard('PRODUCTION_BOARD')],
        loadComponent: () => import('./features/admin/production-board/production-board.component').then(m => m.ProductionBoardComponent)
      },
      {
        path: 'reports', // Relatórios de vendas do período (só administradores)
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/reports/reports.component').then(m => m.ReportsComponent)
      },
      {
        path: 'customers',
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/customers-manager/customers-manager.component').then(m => m.CustomersManagerComponent)
      },
      {
        path: 'delivery', // Fretes: valores de entrega e os bairros de cada um
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/delivery-manager/delivery-manager.component').then(m => m.DeliveryManagerComponent)
      },
      {
        path: 'kits',
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/kit-manager/kit-manager.component').then(m => m.KitManagerComponent)
      },
      {
        path: 'banners', // Slides do carrossel da vitrine
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/banner-manager/banner-manager.component').then(m => m.BannerManagerComponent)
      },
      {
        path: 'payments', // Formas de pagamento, taxas e parcelamento
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/payment-manager/payment-manager.component').then(m => m.PaymentManagerComponent)
      },
      {
        path: 'whatsapp', // Instância do WhatsApp da empresa (QR Code)
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/whatsapp-manager/whatsapp-manager.component').then(m => m.WhatsappManagerComponent)
      },
      {
        path: 'settings', // Dados da empresa: nome, chave PIX, WhatsApp dos avisos, link da loja
        canActivate: [adminAccessGuard()],
        loadComponent: () => import('./features/admin/company-settings/company-settings.component').then(m => m.CompanySettingsComponent)
      },
      // Assinatura e conta ficam sempre acessíveis: é por elas que uma empresa vencida volta a ficar em dia
      {
        path: 'subscription',
        loadComponent: () => import('./features/admin/subscription-manager/subscription-manager.component').then(m => m.SubscriptionManagerComponent)
      },
      {
        path: 'account',
        loadComponent: () => import('./features/client/profile/profile.component').then(m => m.ProfileComponent)
      }
    ]
  },

  // ---------------------------------------------------------------------------
  // Administrador geral do sistema (senha em SUPER_ADMIN_PASSWORD na API)
  // ---------------------------------------------------------------------------
  {
    path: 'super-admin/login',
    canActivate: [superAdminLoginGuard],
    loadComponent: () => import('./features/super-admin/super-admin-login.component').then(m => m.SuperAdminLoginComponent)
  },
  {
    path: 'super-admin',
    canActivate: [superAdminGuard],
    loadComponent: () => import('./features/super-admin/super-admin-shell.component').then(m => m.SuperAdminShellComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./features/super-admin/super-admin-dashboard.component').then(m => m.SuperAdminDashboardComponent)
      },
      {
        path: 'empresas',
        loadComponent: () => import('./features/super-admin/super-admin-tenants.component').then(m => m.SuperAdminTenantsComponent)
      },
      {
        path: 'empresas/:id',
        loadComponent: () => import('./features/super-admin/super-admin-tenant-detail.component').then(m => m.SuperAdminTenantDetailComponent)
      },
      {
        path: 'mensalidades',
        loadComponent: () => import('./features/super-admin/super-admin-payments.component').then(m => m.SuperAdminPaymentsComponent)
      },
      {
        path: 'avisos',
        loadComponent: () => import('./features/super-admin/super-admin-notices.component').then(m => m.SuperAdminNoticesComponent)
      }
    ]
  },

  // ---------------------------------------------------------------------------
  // Loja de cada empresa: /<slug>. Fica por último para não capturar as rotas acima.
  // ---------------------------------------------------------------------------
  {
    path: ':slug',
    canActivate: [storeGuard],
    loadComponent: () => import('./features/client/store-shell/store-shell.component').then(m => m.StoreShellComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./features/client/home/home.component').then(m => m.HomeComponent)
      },
      {
        path: 'login', // Login e cadastro dos clientes desta loja
        loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'build-kit/:id', // Rota pública para montar o kit
        loadComponent: () => import('./features/client/kit-builder/kit-builder.component').then(m => m.KitBuilderComponent)
      },
      {
        path: 'checkout', // Rota pública: o login só é exigido para confirmar o pedido
        loadComponent: () => import('./features/client/checkout/checkout.component').then(m => m.CheckoutComponent)
      },
      {
        path: 'my-orders', // Acompanhamento do pedido: só nos planos com o módulo
        canActivate: [storeModuleGuard('ORDER_TRACKING'), customerGuard],
        loadComponent: () => import('./features/client/my-orders/my-orders.component').then(m => m.MyOrdersComponent)
      },
      {
        path: 'profile',
        canActivate: [customerGuard],
        loadComponent: () => import('./features/client/profile/profile.component').then(m => m.ProfileComponent)
      }
    ]
  },
  {
    path: '**',
    redirectTo: ''
  }
];
